'use strict';

// Client-side HIBP check (ST4S A2, R7) on ForgotPasswordSubmit, the forced password change and
// generated passphrases, before the password reaches Cognito. A usability check, not
// enforcement: it fails open (any error means "not known to be breached"); the pool and, if
// enabled, Cognito threat protection are the server-side checks on that path.
//
// k-anonymity: only the first 5 hex characters of the SHA-1 leave the browser
// (https://haveibeenpwned.com/API/v3#SearchingPwnedPasswordsByRange), with Add-Padding.
//
// Switch it off (no network) with window.SM_HIBP_DISABLED = true or
// localStorage.smHibpDisabled = "1" (Playwright, local runs). Unit tests inject fetch/digest.

const HIBP_RANGE_URL = 'https://api.pwnedpasswords.com/range/';
const HIBP_TIMEOUT_MS = 3000;

const isHibpDisabled = () => {
    try {
        if (typeof window !== 'undefined' && window.SM_HIBP_DISABLED === true) { return true; }
        if (typeof localStorage !== 'undefined' && localStorage.getItem('smHibpDisabled') === '1') { return true; }
    } catch (e) {
        // storage unavailable: leave the check on
    }
    return false;
};

const toHex = (buffer) =>
    Array.from(new Uint8Array(buffer)).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();

// SHA-1 via crypto.subtle; returns upper-case hex.
const sha1Hex = async (text) => {
    const bytes = new TextEncoder().encode(text);
    return toHex(await crypto.subtle.digest('SHA-1', bytes));
};

const suffixCountInRange = (body, suffix) => {
    for (const line of body.split('\n')) {
        const [lineSuffix, count] = line.trim().split(':');
        if (lineSuffix && lineSuffix.toUpperCase() === suffix) {
            return parseInt(count, 10) || 0;
        }
    }
    return 0;
};

// Resolves true only when HIBP says the password has been breached.
const isPasswordBreached = async (password, options = {}) => {
    const disabled = options.disabled !== undefined ? options.disabled : isHibpDisabled();
    if (disabled) { return false; }
    const fetchImpl = options.fetchImpl || (typeof fetch !== 'undefined' ? fetch : undefined);
    const digest = options.digest || sha1Hex;
    const timeoutMs = options.timeoutMs !== undefined ? options.timeoutMs : HIBP_TIMEOUT_MS;
    let timer;
    try {
        if (!fetchImpl) { return false; }
        const hash = await digest(password);
        const prefix = hash.slice(0, 5);
        const suffix = hash.slice(5);
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
        const timeout = new Promise((_resolve, reject) => {
            timer = setTimeout(() => {
                if (controller) { controller.abort(); }
                reject(new Error('HIBP timeout'));
            }, timeoutMs);
        });
        const request = (async () => {
            const resp = await fetchImpl(HIBP_RANGE_URL + prefix, {
                headers: { 'Add-Padding': 'true' },
                signal: controller ? controller.signal : undefined,
            });
            if (!resp.ok) { throw new Error('HTTP ' + resp.status); }
            return resp.text();
        })();
        const body = await Promise.race([request, timeout]);
        return suffixCountInRange(body, suffix) > 0;
    } catch (e) {
        return false;
    } finally {
        clearTimeout(timer);
    }
};

const BREACHED_PASSWORD_MESSAGE = 'This password appears in known data breaches. Please choose another.';

module.exports = { HIBP_RANGE_URL, isHibpDisabled, isPasswordBreached, sha1Hex, suffixCountInRange, BREACHED_PASSWORD_MESSAGE };
